import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Dropdown,
  Menu,
  Badge,
  Segmented,
  Card,
  Tooltip,
  Tag,
  Space,
  Skeleton,
  Flex,
  message,
  Spin,
  Empty,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  InboxOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import "./EventDashboard.css";
import Event from "./Events.jsx";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SuccessModal from "./SuccessModal.jsx";
import RejectionModal from "../Approval/RejectionModal.jsx";
dayjs.extend(customParseFormat);

function EventDashboard() {
  const [filterType, setFilterType] = useState("All");
  const [isEventModalVisible, setIsEventModalVisible] = useState(false);
  const [eventData, setEventData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("Type");
  const [published, setPublished] = useState("All")
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [editEventData, setEditEventData] = useState(null)
  const [pageSize, setPageSize] = useState(10);
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [eventCurrentData, setEventCurrentData] = useState(null)
  const [openRejectionModal, setOpenRejectionModal] = useState(false);
  const [publishEvent, setPublishEvent] = useState(false)
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  const handleOpenEventModal = () => {
    setIsEventModalVisible(true)
    setEditEventData(null)
  }
  const handleCloseEventModal = () => setIsEventModalVisible(false);
  const handleEventSuccess = () => getEventData();

  // 📦 Fetch Event Data
  const getEventData = async () => {
    setLoading(true);
    try {
      const requestData = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };

      const response = await api.getEventCreation(requestData);

      if (response?.data?.status === "1") {
        const events = (response.data.events_data || []).sort(
          (a, b) =>
            new Date(
              b.fld_system_inserted_datetime || b.fld_created_date || 0
            ) -
            new Date(
              a.fld_system_inserted_datetime || a.fld_created_date || 0
            )
        );

        setEventData(events);
        setFilteredData(events);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEventData();
  }, []);



  // --- Publish Event API Call ---
  const handlePublishEvent = async () => {
    try {
      if (!eventCurrentData?.fld_rf_id) {
        messageApi.error("Event ID not found!");
        return;
      }

      const requestData = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "201",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
        fld_rf_id: eventCurrentData.fld_rf_id,
      };

      const response = await api.publishEvent(requestData);

      if (response?.data?.status === "1") {
        setPublishSuccess(true);
        setEventCurrentData(null)
        setPublishEvent(false)
        setOpenRejectionModal(false)
        messageApi.success("Event published successfully!");
        getEventData();
      } else {
        messageApi.error(response?.data?.message || "Failed to publish event");
      }
    } catch (error) {
      console.error("Error publishing event:", error);
      messageApi.error("Something went wrong while publishing the event");
    }
  };


  // 🔍 Search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = eventData.filter((ev) =>
      ev.fld_event_title.toLowerCase().includes(value)
    );
    setFilteredData(filtered);
    setCurrentPage(1); // reset to page 1 after search
  };

  // 🧩 Filter by Type (Online/Offline)
  const typeMenu = (
    <Menu
      items={[
        {
          key: "Online",
          label: "Online",
          onClick: () => {
            setSelectedType("Online");
            setFilteredData(
              eventData.filter((ev) => ev.fld_event_type_name === "Online")
            );
            setCurrentPage(1);
          },
        },
        {
          key: "Offline",
          label: "Offline",
          onClick: () => {
            setSelectedType("Offline");
            setFilteredData(
              eventData.filter((ev) => ev.fld_event_type_name === "Offline")
            );
            setCurrentPage(1);
          },
        },
      ]}
    />
  );

  const publishMenu = (
    <Menu
      items={[
        {
          key: "Published",
          label: "Published",
          onClick: () => {
            setPublished("Published");
            setFilteredData(
              eventData.filter((ev) => ev.fld_approved_name.toLowerCase() === "published")
            );
            setCurrentPage(1);
          },
        },
        {
          key: "Unpublished",
          label: "Unpublished",
          onClick: () => {
            setPublished("Unpublished");
            setFilteredData(
              eventData.filter((ev) => ev.fld_approved_name.toLowerCase() === "pending")
            );
            setCurrentPage(1);
          },
        },
      ]}

    />
  )

  // 📅 Filter by Event Status (All/Upcoming/Complete)
  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    setCurrentPage(1);

    const now = dayjs(); // current datetime

    switch (type) {
      case "Upcoming":
        // Events where event start datetime is after current datetime
        setFilteredData(
          eventData.filter((event) => {
            const eventDateTime = dayjs(event.fld_start_date_time, "DD-MM-YYYY HH:mm:ss");
            return eventDateTime.isAfter(now);
          })
        );
        break;

      case "Complete":
        // Events where event start datetime is before or equal to current datetime
        setFilteredData(
          eventData.filter((event) => {
            const eventDateTime = dayjs(event.fld_start_date_time, "DD-MM-YYYY HH:mm:ss");
            return eventDateTime.isSame(now) || eventDateTime.isBefore(now);
          })
        );
        break;

      case "All":
      default:
        setFilteredData(eventData);
        break;
    }
  };

  const handleEditEvent = (data) => {
    setIsEventModalVisible(true)
    setEditEventData(data)
  }

  const handleDeleteEvent = (data) => {
    setOpenRejectionModal(true)
    setEventCurrentData(data)

  }

  const handleDeleteApi = async (reason) => {
    try {
      if (eventCurrentData) {
        const requestData = {
          "synceddatetime": "2025-05-29 10:08:00",
          "FormCode": "201",
          "AppVersion": "1.0.0.0",
          "ApiKey": "kavin",
          "AppTypeNo": "3",
          "fld_rf_id": eventCurrentData?.fld_rf_id || "",
          "fld_event_sw_id": eventCurrentData?.fld_event_sw_id,
          "fld_reason_for_deletion": reason ? reason : ""
        }

        const response = await api.deleteEvent(requestData)

        if (response.data.status === "1") {
          setDeleteSuccess(true)
          setEventCurrentData(null)
          getEventData()

        }
      }
    }

    catch (error) {
      console.error("error deleting event:", error);
    }

  }



  const handleResetFilters = () => {
    setSelectedType("Type");
    setFilteredData(eventData);
    setPublished("All")
    setSearchTerm("");
    setFilterType("All");
    setCurrentPage(1);
    messageApi.success("Filters reset successfully");
  };

  // 📄 Page size selection
  const handlePageSizeChange = (size) => {
    setPageSize(Number(size));
    setCurrentPage(1);
  };

  const pageMenu = (
    <Menu
      items={[5, 10, 25, 50].map((num) => ({
        key: num,
        label: num.toString(),
        onClick: () => handlePageSizeChange(num),
      }))}
    />
  );

  // 🔢 Pagination Logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const EventSkeleton = () => (
    <Card className="review-card">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ width: 30 }}>
          <Skeleton.Input
            active
            size="small"
            style={{ width: 20, height: 16 }}
          />
        </div>

        <div style={{ flex: 1 }}>
          <Skeleton.Input
            active
            size="small"
            style={{
              width: "40%",
              height: 16,
              marginBottom: 8,
            }}
          />

          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <Skeleton.Button
              active
              size="small"
              style={{ width: 80 }}
            />

            <Skeleton.Button
              active
              size="small"
              style={{ width: 90 }}
            />
          </div>

          <Skeleton.Input
            active
            size="small"
            style={{
              width: "60%",
              height: 14,
            }}
          />
        </div>

        <Space>
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
          <Skeleton.Button active size="small" />
        </Space>
      </div>
    </Card>
  );


  return (

    <div className="review-page">
      {contextHolder}
      {/* 🔍 Header */}
      <Flex className="review-header" justify="space-between" align="center">
        <Input
          className="review-search"
          prefix={<SearchOutlined />}
          placeholder="Search Event Title..."
          value={searchTerm}
          onChange={handleSearch}
        />
        <Space>
          {/* <Button icon={<DownloadOutlined />} className="review-export-btn">
              Export
            </Button> */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenEventModal}
            style={{ backgroundColor: "#0072B1" }}
          >
            Add New Event
          </Button>
        </Space>
      </Flex>

      {/* 📋 Content */}
      <div className="review-container">
        <div className="review-heading">
          <div>
            <h2 className="review-title">Event Management</h2>
            <div className="review-subtitle">Manage and publish your events</div>
          </div>
        </div>

        {/* 🔘 Filters */}
        <div className="review-filters">
          <div className="filter-buttons">
            {/* <Button icon={<FilterOutlined />} /> */}

            <Dropdown overlay={typeMenu} trigger={["click"]}>
              <Button>
                {selectedType} <DownOutlined />
              </Button>
            </Dropdown>

            <Dropdown overlay={publishMenu} trigger={["click"]}>
              <Button>
                {published} <DownOutlined />
              </Button>
            </Dropdown>

            <Button
              icon={<ReloadOutlined />}
              className="reset-btn"
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </div>

          <Segmented
            className="review-toggle"
            options={["All", "Upcoming", "Complete"]}
            value={filterType}

            onChange={handleFilterTypeChange}
          />
        </div>

        {/* 🗂️ Event Cards */}
        <div className="review-card-list">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <EventSkeleton key={index} />
            ))
          ) : paginatedData.length === 0 ? (
            <div >
              <Card
                className="nodata-card"
              >
                <Empty
                  description="No data available "
                  image={<InboxOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
                />
              </Card>
            </div>
          ) : (
            (paginatedData.length > 0 ? paginatedData : []).map(
              (event, index) => (
                <Card
                  key={event.fld_event_id || index}
                  className="review-card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`event/${event.fld_event_id}`, {
                    state: { eventData: event },
                  })}
                >
                  <div className="event-content">
                    <div className="event-number">
                      {startIndex + index + 1}
                    </div>

                    <div className="event-main">
                      {/* 🔹 Title and Date/Time on same line */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 20,
                        }}
                      >
                        <span
                          className="event-name"
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: "#222",
                            width: '50%'
                          }}
                        >
                          {event.fld_event_title || "Untitled Event"}
                        </span>

                        {event.fld_start_date_time && (
                          <span
                            className="event-date-time"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              color: "#555",
                              fontSize: 11,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <CalendarOutlined style={{ fontSize: 11, color: "#0072B1" }} />
                            {dayjs(event.fld_start_date_time, "DD-MM-YYYY HH:mm:ss").format(
                              "DD MMM YYYY, hh:mm A"
                            )}
                          </span>
                        )}
                      </div>

                      {/* 🔹 Second line: Venue, Type, Status */}
                      <div
                        className="event-tags"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 4,
                        }}
                      >
                        {event.fld_venue && (
                          <span
                            className="event-location"
                            style={{ display: "flex", alignItems: "center", gap: 4 }}
                          >
                            <EnvironmentOutlined style={{ fontSize: 12, color: "#0072B1" }} />
                            {event.fld_venue}
                          </span>
                        )}
                        <Tag
                          color={
                            event.fld_event_type_name === "Online"
                              ? "cyan"
                              : "blue"
                          }
                        >
                          {event.fld_event_type_name || "Type"}
                        </Tag>

                        <Tag
                          color={
                            event.fld_approved_name?.toLowerCase() === "published"
                              ? "green"
                              : "red"
                          }
                        >
                          {event.fld_approved_name?.toLowerCase() === "published"
                            ? "Published"
                            : "Unpublished"}
                        </Tag>
                      </div>
                    </div>



                    {/* Actions */}
                    <Space size={8} className="event-actions">
                      {/* <Tooltip title="View Event Details">
                          <Button
                            icon={<EyeOutlined />}
                            size="small"
                            style={{
                              borderRadius: 6,
                              borderColor: "#ccc",
                              color: "#555",
                              background: "white",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`event/${event.fld_event_id}`, {
                                state: { eventData: event },
                              })
                            }

                            }
                          >


                            View
                          </Button>
                        </Tooltip> */}
                      {event.fld_approved_name.toLowerCase() !== 'published' &&
                        <>
                          <Tooltip title="Edit this Event">
                            <Button
                              icon={<EditOutlined />}
                              size="small"
                              disabled={event.fld_approved_name.toLowerCase() === "published"}
                              style={{
                                borderRadius: 6,
                                borderColor: "#ccc",
                                color: "#555",
                                background: "white",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditEvent(event)
                              }
                              }
                            >
                              Edit
                            </Button>
                          </Tooltip>

                          <Tooltip title="Delete this Event">
                            <Button
                              icon={<DeleteOutlined />}
                              size="small"
                              disabled={event.fld_approved_name.toLowerCase() === "published"}
                              style={{
                                borderRadius: 6,
                                borderColor: "#ccc",
                                color: "#d9534f",
                                background: "white",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event)
                              }}
                            >
                              Delete
                            </Button>
                          </Tooltip></>
                      }

                      <Tooltip title={event.fld_approved_name.toLowerCase() === 'published' ? 'Published' : 'Publish'}>
                        <Button
                          type="primary"
                          size="small"
                          className="publish-btn"
                          disabled={event.fld_approved_name.toLowerCase() === 'published'}
                          style={{ backgroundColor: event.fld_approved_name.toLowerCase() === 'published' ? "#dbe2e6ff" : "#0072B1" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenRejectionModal(true)
                            setEventCurrentData(event)
                            setPublishEvent(true)
                          }
                          }
                        >
                          {event.fld_approved_name.toLowerCase() === 'published' ? 'Published' : 'Publish'}
                        </Button>
                      </Tooltip>

                    </Space>
                  </div>
                </Card>
              )
            ))}
        </div>

        {/* 📄 Pagination */}

        <div className="review-pagination">
          <Button
            icon={<LeftOutlined />}
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          />
          <span className="page-info">
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            icon={<RightOutlined />}
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
          />
          <Dropdown overlay={pageMenu} trigger={["click"]}>
            <Button>
              {pageSize} / page <DownOutlined />
            </Button>
          </Dropdown>
        </div>


      </div>

      {/* 🆕 Add Event Modal */}
      {isEventModalVisible && (
        <Event
          visible={isEventModalVisible}
          onClose={handleCloseEventModal}
          onSuccess={handleEventSuccess}
          editdata={editEventData}
          getEventData={getEventData}
        />)}
      {openRejectionModal === true &&
        <RejectionModal
          title={publishEvent ? 'Publish Event' : 'Delete Event'}
          content={publishEvent ? 'Are you sure you want to Publish this Event ?' : 'Are you sure you want to Delete this Event ?  you cannot undo this change'}
          buttonName={publishEvent ? 'Publish' : 'Delete'}
          open={openRejectionModal}
          onClose={() => setOpenRejectionModal(false)}
          onSubmit={(reason) => publishEvent ?
            handlePublishEvent() :
            handleDeleteApi(reason)}
        />
      }


      {publishSuccess === true && (
        <SuccessModal
          visible={publishSuccess}
          onClose={() => setPublishSuccess(false)}
          title="Event Published Successfully"
          description="Your event has been successfully published and is now live!"
        />
      )}



      {deleteSuccess === true &&
        <SuccessModal
          visible={deleteSuccess}
          onClose={() => setDeleteSuccess(false)}
          title={"Event Deleted Successfully"}
          description="Your Event Has been Successfully Deleted!"
        />
      }

    </div>
  );
}

export default EventDashboard;